import React, { useState } from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Row,
  Col,
  Card,
  CardHeader,
  Alert,
} from "reactstrap";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { create } from "zustand";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Column } from "react-table";
import { useTranslation } from "react-i18next";
import { encryptData } from "../../helpers/crypto_utils";
import { translateApiError } from "../../helpers/translateApiError";
import { setAuthorization } from "../../helpers/api_helper";
// API functions
import {
  fetchUsers,
  deleteUser,
  fetchUserLoginLogs,
  createUser,
  updateUser,
  resendUserInvitation,
  activateUser,
  deactivateUser,
  impersonateUser,
  checkUserPermission,
  getUserRole,
} from "./api/usersApi";

// Components
import TableContainer, {
  SelectFilterOptionGroup,
} from "../../components/Common/TableContainer";
import UserModal, { UserFormValues } from "./UserModal";
import ConfirmActionModal from "../../components/Common/ConfirmActionModal";
import useAuthUser from "../../components/Hooks/useAuthUser";

// Styles
import "./styles.css";
import formatPersonName from "../../helpers/formatPersonName";
import { formatDate as formatDay, formatDateTime } from "../../helpers/dateFormat";

// "Chrome · macOS" from a user agent string; the full string shows on hover.
const describeDevice = (userAgent?: string): string => {
  if (!userAgent) return "-";
  const browser = /Edg\//.test(userAgent)
    ? "Edge"
    : /OPR\//.test(userAgent)
    ? "Opera"
    : /Chrome\//.test(userAgent)
    ? "Chrome"
    : /Firefox\//.test(userAgent)
    ? "Firefox"
    : /Safari\//.test(userAgent)
    ? "Safari"
    : "";
  const os = /iPhone|iPad/.test(userAgent)
    ? "iOS"
    : /Android/.test(userAgent)
    ? "Android"
    : /Mac OS X/.test(userAgent)
    ? "macOS"
    : /Windows/.test(userAgent)
    ? "Windows"
    : /Linux/.test(userAgent)
    ? "Linux"
    : "";
  return [browser, os].filter(Boolean).join(" · ") || userAgent;
};

// Types
interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role?: string;
  locked?: boolean;
  lockedUntil?: string | null;
  lastLogin?: string | null;
  status?: string; // ACTIVE | INACTIVE | PENDING | EXPIRED
  createdAt?: string;
}

interface PageParams {
  rowsPerPage: number;
  currentPage: number;
}

interface FilterParams {
  search: string;
  status: string;
  dateRange: any;
}

interface UsersStoreState {
  addUsersModal: boolean;
  deleteUsersModal: boolean;
  usersStatusModal: boolean;
  loginLogsModal: boolean;
  userAccountModal: boolean;
  editUserModal: boolean;
  confirmActivateModal: boolean;
  confirmDeactivateModal: boolean;
  confirmLoginAsUserModal: boolean;
  successModal: boolean;
  createErrorModal: boolean;
  resendSuccessModal: boolean;
  resendErrorModal: boolean;
  usersToDelete: Partial<User>;
  usersToUpdate: { id: string; status: string };
  failedUserData: UserFormValues | null;
  createErrorMessage: string | null;
  selectedUserForLogs: User | null;
  selectedUserForAccount: User | null;
  selectedUserForEdit: User | null;
  selectedUserForActivate: User | null;
  selectedUserForDeactivate: User | null;
  selectedUserForLoginAs: User | null;
  pageParams: PageParams;
  filterParams: FilterParams;
  setAddUsersModal: (open: boolean) => void;
  setDeleteUsersModal: (open: boolean) => void;
  setLoginLogsModal: (open: boolean) => void;
  setUserAccountModal: (open: boolean) => void;
  setEditUserModal: (open: boolean) => void;
  setConfirmActivateModal: (open: boolean) => void;
  setConfirmDeactivateModal: (open: boolean) => void;
  setConfirmLoginAsUserModal: (open: boolean) => void;
  setSuccessModal: (open: boolean) => void;
  setCreateErrorModal: (open: boolean) => void;
  setResendSuccessModal: (open: boolean) => void;
  setResendErrorModal: (open: boolean) => void;
  setFailedUserData: (data: UserFormValues | null) => void;
  setCreateErrorMessage: (message: string | null) => void;
  setUsersToDelete: (item: Partial<User>) => void;
  setUsersToUpdate: (item: { id: string; status: string }) => void;
  setSelectedUserForLogs: (user: User | null) => void;
  setSelectedUserForAccount: (user: User | null) => void;
  setSelectedUserForEdit: (user: User | null) => void;
  setSelectedUserForActivate: (user: User | null) => void;
  setSelectedUserForDeactivate: (user: User | null) => void;
  setSelectedUserForLoginAs: (user: User | null) => void;
  setPageParams: (params: Partial<PageParams>) => void;
  setFilterParams: (params: Partial<FilterParams>) => void;
  resetModals: () => void;
}

type UserAction =
  | "viewLogs"
  | "manage"
  | "delete"
  | "resendInvite"
  | "activate"
  | "deactivate"
  | "loginAsUser";

// Zustand store for local UI state
const useUsersStore = create<UsersStoreState>((set) => ({
  // Modal states
  addUsersModal: false,
  deleteUsersModal: false,
  usersStatusModal: false,
  loginLogsModal: false,
  userAccountModal: false,
  editUserModal: false,
  confirmActivateModal: false,
  confirmDeactivateModal: false,
  confirmLoginAsUserModal: false,
  successModal: false,
  createErrorModal: false,
  resendSuccessModal: false,
  resendErrorModal: false,

  // Selected items
  failedUserData: null,
  createErrorMessage: null,
  usersToDelete: {},
  usersToUpdate: { id: "", status: "" },
  selectedUserForLogs: null,
  selectedUserForAccount: null,
  selectedUserForEdit: null,
  selectedUserForActivate: null,
  selectedUserForDeactivate: null,
  selectedUserForLoginAs: null,

  // Pagination & filters
  pageParams: { rowsPerPage: 10, currentPage: 1 },
  filterParams: { search: "", status: "", dateRange: null },

  // Actions
  setAddUsersModal: (open: boolean) => set({ addUsersModal: open }),
  setDeleteUsersModal: (open: boolean) => set({ deleteUsersModal: open }),
  setLoginLogsModal: (open: boolean) => set({ loginLogsModal: open }),
  setUserAccountModal: (open: boolean) => set({ userAccountModal: open }),
  setEditUserModal: (open: boolean) => set({ editUserModal: open }),
  setConfirmActivateModal: (open: boolean) =>
    set({ confirmActivateModal: open }),
  setConfirmDeactivateModal: (open: boolean) =>
    set({ confirmDeactivateModal: open }),
  setConfirmLoginAsUserModal: (open: boolean) =>
    set({ confirmLoginAsUserModal: open }),
  setSuccessModal: (open: boolean) => set({ successModal: open }),
  setCreateErrorModal: (open: boolean) => set({ createErrorModal: open }),
  setResendSuccessModal: (open: boolean) => set({ resendSuccessModal: open }),
  setResendErrorModal: (open: boolean) => set({ resendErrorModal: open }),
  setFailedUserData: (data: UserFormValues | null) =>
    set({ failedUserData: data }),
  setCreateErrorMessage: (message: string | null) =>
    set({ createErrorMessage: message }),
  setUsersToDelete: (item: Partial<User>) => set({ usersToDelete: item }),
  setUsersToUpdate: (item: { id: string; status: string }) =>
    set({ usersToUpdate: item }),
  setSelectedUserForLogs: (user: User | null) =>
    set({ selectedUserForLogs: user }),
  setSelectedUserForAccount: (user: User | null) =>
    set({ selectedUserForAccount: user }),
  setSelectedUserForEdit: (user: User | null) =>
    set({ selectedUserForEdit: user }),
  setSelectedUserForActivate: (user: User | null) =>
    set({ selectedUserForActivate: user }),
  setSelectedUserForDeactivate: (user: User | null) =>
    set({ selectedUserForDeactivate: user }),
  setSelectedUserForLoginAs: (user: User | null) =>
    set({ selectedUserForLoginAs: user }),
  setPageParams: (params: Partial<PageParams>) =>
    set((state) => ({
      pageParams: { ...state.pageParams, ...params },
    })),
  setFilterParams: (params: Partial<FilterParams>) =>
    set((state) => ({
      filterParams: { ...state.filterParams, ...params },
    })),

  // Reset functions
  resetModals: () =>
    set({
      addUsersModal: false,
      deleteUsersModal: false,
      usersStatusModal: false,
      loginLogsModal: false,
      userAccountModal: false,
      editUserModal: false,
      confirmActivateModal: false,
      confirmDeactivateModal: false,
      confirmLoginAsUserModal: false,
      successModal: false,
      createErrorModal: false,
      resendSuccessModal: false,
      resendErrorModal: false,
      failedUserData: null,
      createErrorMessage: null,
      usersToDelete: {},
      usersToUpdate: { id: "", status: "" },
      selectedUserForLogs: null,
      selectedUserForAccount: null,
      selectedUserForEdit: null,
      selectedUserForActivate: null,
      selectedUserForDeactivate: null,
      selectedUserForLoginAs: null,
    }),
}));

const UsersList: React.FC = () => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { userProfile } = useAuthUser();

  // State for user role and permissions
  const [currentUserRole, setCurrentUserRole] = React.useState<string | null>(
    null
  );
  const [canCreate, setCanCreate] = React.useState(false);
  const [canEdit, setCanEdit] = React.useState(false);
  const [canDelete, setCanDelete] = React.useState(false);
  const [canActivate, setCanActivate] = React.useState(false);
  const [canDeactivate, setCanDeactivate] = React.useState(false);
  const [canLoginAs, setCanLoginAs] = React.useState(false);

  // Fetch user role and permissions
  React.useEffect(() => {
    const loadPermissions = () => {
      const role = getUserRole();
      setCurrentUserRole(role);
      setCanCreate(checkUserPermission("create"));
      setCanEdit(checkUserPermission("edit"));
      setCanDelete(checkUserPermission("delete"));
      setCanActivate(checkUserPermission("activate"));
      setCanDeactivate(checkUserPermission("deactivate"));
      setCanLoginAs(checkUserPermission("loginAs"));
    };
    loadPermissions();
  }, []);

  const isSuperAdminOrAdmin =
    currentUserRole === "SUPER_ADMIN" || currentUserRole === "ADMIN";
  const canViewLogs = checkUserPermission("viewLogs");
  const canResendInvite = checkUserPermission("resendInvite");

  // Zustand state
  const {
    addUsersModal,
    deleteUsersModal,
    loginLogsModal,
    editUserModal,
    confirmActivateModal,
    confirmDeactivateModal,
    confirmLoginAsUserModal,
    successModal,
    createErrorModal,
    resendSuccessModal,
    resendErrorModal,
    failedUserData,
    createErrorMessage,
    usersToDelete,
    selectedUserForLogs,
    selectedUserForEdit,
    selectedUserForActivate,
    selectedUserForDeactivate,
    selectedUserForLoginAs,
    pageParams,
    filterParams,
    setAddUsersModal,
    setDeleteUsersModal,
    setLoginLogsModal,
    setEditUserModal,
    setConfirmActivateModal,
    setConfirmDeactivateModal,
    setConfirmLoginAsUserModal,
    setSuccessModal,
    setCreateErrorModal,
    setResendSuccessModal,
    setResendErrorModal,
    setFailedUserData,
    setCreateErrorMessage,
    setUsersToDelete,
    setSelectedUserForLogs,
    // setSelectedUserForAccount, // Not used in this component
    setSelectedUserForEdit,
    setSelectedUserForActivate,
    setSelectedUserForDeactivate,
    setSelectedUserForLoginAs,
    setPageParams,
    setFilterParams,
    resetModals,
  } = useUsersStore();

  // Error state for toast notifications
  const [apiError, setApiError] = useState<any>(null);

  // Get user identity for cache key - ensures data refreshes when user changes
  const userIdentity = userProfile?.email || "";

  // TanStack Query for fetching users (backend handles role-based filtering)
  const {
    data: usersData,
    isLoading: isUsersLoading,
    error: usersError,
  } = useQuery({
    queryKey: [
      "users",
      pageParams,
      filterParams,
      userIdentity,
      currentUserRole,
    ],
    queryFn: async () => {
      // Transform params to match backend API expectations
      const backendParams: any = {
        page: pageParams.currentPage,
        take: pageParams.rowsPerPage,
      };

      // Only add search if it has a value
      if (filterParams.search) {
        backendParams.search = filterParams.search;
      }

      // Only add status if it has a valid value (not "all" or empty)
      if (filterParams.status && filterParams.status !== "all") {
        backendParams.status = filterParams.status.toUpperCase();
      }

      // For SUPER_ADMIN and ADMIN, only show SUPER_ADMIN and ADMIN users
      if (isSuperAdminOrAdmin) {
        backendParams.roles = ["SUPER_ADMIN", "ADMIN"];
      }

      console.log("Backend params being sent:", backendParams);
      const response = await fetchUsers(backendParams);
      console.log("Response received:", response);
      return response;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // TanStack Query for fetching user login logs
  const { data: loginLogsData, isLoading: isLoginLogsLoading } = useQuery({
    queryKey: ["userLoginLogs", selectedUserForLogs?.id],
    queryFn: () => fetchUserLoginLogs(selectedUserForLogs?.id || ""),
    enabled: !!selectedUserForLogs?.id && loginLogsModal,
  });

  // Mutations for CRUD operations
  const deleteUsersMutation = useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      resetModals();
    },
  });

  const createUserMutation = useMutation({
    mutationFn: (userData: UserFormValues) => createUser(userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      setAddUsersModal(false);
      setSuccessModal(true);
      setFailedUserData(null); // Clear failed data on success
    },
    onError: (error: any, variables) => {
      setFailedUserData(variables); // Store the failed form values
      setCreateErrorMessage(
        translateApiError(error?.message, t("users.errors.createFailed"))
      ); // Store the error message
      setAddUsersModal(false);
      setCreateErrorModal(true);
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, userData }: { id: string; userData: UserFormValues }) =>
      updateUser(id, userData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      resetModals();
    },
    onError: (error: any) => {
      const errorMessage =
        error?.data?.errors?.[0] || error?.data?.message || error?.message;
      setApiError({
        message: translateApiError(
          errorMessage,
          t("users.errors.updateFailed")
        ),
      });
    },
  });

  const resendInvitationMutation = useMutation({
    mutationFn: (userId: string) => resendUserInvitation(userId),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      // Check if response has success field
      if (response?.success) {
        setResendSuccessModal(true);
      } else {
        setResendErrorModal(true);
      }
    },
    onError: () => {
      setResendErrorModal(true);
    },
  });

  const activateUserMutation = useMutation({
    mutationFn: (userId: string) => activateUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      resetModals();
    },
    onError: (error: any) => {
      const errorMessage =
        error?.data?.errors?.[0] || error?.data?.message || error?.message;
      setApiError({
        message: translateApiError(
          errorMessage,
          t("users.errors.activateFailed")
        ),
      });
      resetModals();
    },
  });

  const deactivateUserMutation = useMutation({
    mutationFn: (userId: string) => deactivateUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      resetModals();
    },
    onError: (error: any) => {
      const errorMessage =
        error?.data?.errors?.[0] || error?.data?.message || error?.message;
      setApiError({
        message: translateApiError(
          errorMessage,
          t("users.errors.deactivateFailed")
        ),
      });
      resetModals();
    },
  });

  const impersonateUserMutation = useMutation({
    mutationFn: (userId: string) => impersonateUser(userId),
    onSuccess: (response: any) => {
      // Store the new token and user data in authUser format
      if (response?.token && response?.user) {
        // Create auth object matching login saga format
        const authUser = {
          token: response.token,
          userProfile: {
            email: response.user.email,
            first_name: response.user.firstName,
            last_name: response.user.lastName,
          },
          ...(response.tenants &&
            response.tenants.length > 0 && {
              tenants: response.tenants,
              tenant: response.tenants[0], // Primary tenant
            }),
          roles: response.user.roles,
          isSuperAdmin: response.user.roles?.includes("SUPER_ADMIN") || false,
          isAdmin:
            response.user.roles?.includes("ADMIN") ||
            response.user.roles?.includes("SUPER_ADMIN") ||
            false,
          isTenant:
            response.user.roles?.includes("TENANT_ADMIN") ||
            response.user.roles?.includes("TENANT_USER") ||
            false,
        };

        // Clear old auth data
        localStorage.removeItem("authUser");
        sessionStorage.removeItem("authUser");

        // Store encrypted auth data in localStorage
        localStorage.setItem("authUser", encryptData(authUser));

        // Set authorization header
        setAuthorization(response.token);

        // Force full page reload to refresh all auth state
        window.location.href = "/dashboard";
      }
    },
    onError: (error: any) => {
      const errorMessage =
        error?.data?.errors?.[0] || error?.data?.message || error?.message;
      setApiError({
        message: translateApiError(
          errorMessage,
          t("users.errors.impersonateFailed")
        ),
      });
      // Toast is handled by api_helper interceptor
    },
  });

  // Event handlers
  const handleConfirmDeleteUsers = () => {
    if (usersToDelete?.id) {
      deleteUsersMutation.mutate(usersToDelete.id);
    }
  };

  const handleViewLoginLogs = (user: User) => {
    setSelectedUserForLogs(user);
    setLoginLogsModal(true);
  };

  const handleManageAccount = (user: User) => {
    setSelectedUserForEdit(user);
    setApiError(null);
    setEditUserModal(true);
  };

  const handleCloseModals = () => {
    resetModals();
  };

  const handleCreateUser = (values: UserFormValues) => {
    setApiError(null); // Clear any previous errors
    createUserMutation.mutate(values);
  };

  const handleUpdateUser = (values: UserFormValues) => {
    setApiError(null);
    if (selectedUserForEdit?.id) {
      updateUserMutation.mutate({
        id: selectedUserForEdit.id,
        userData: values,
      });
    }
  };

  const handleOpenCreateUserModal = () => {
    setApiError(null); // Clear any previous errors
    setFailedUserData(null); // Clear any previous failed data to start fresh
    setCreateErrorMessage(null); // Clear any previous error messages
    setAddUsersModal(true);
  };

  const handleConfirmActivate = () => {
    if (selectedUserForActivate?.id) {
      activateUserMutation.mutate(selectedUserForActivate.id);
    }
  };

  const handleConfirmDeactivate = () => {
    if (selectedUserForDeactivate?.id) {
      deactivateUserMutation.mutate(selectedUserForDeactivate.id);
    }
  };

  const handleConfirmLoginAsUser = () => {
    if (selectedUserForLoginAs?.id) {
      impersonateUserMutation.mutate(selectedUserForLoginAs.id);
    }
  };

  // Action handler for dropdown
  const handleAction = (user: User, action: UserAction) => {
    switch (action) {
      case "viewLogs":
        handleViewLoginLogs(user);
        break;
      case "manage":
        handleManageAccount(user);
        break;
      case "delete":
        setUsersToDelete(user);
        setDeleteUsersModal(true);
        break;
      case "resendInvite":
        resendInvitationMutation.mutate(user.id);
        break;
      case "activate":
        setSelectedUserForActivate(user);
        setConfirmActivateModal(true);
        break;
      case "deactivate":
        setSelectedUserForDeactivate(user);
        setConfirmDeactivateModal(true);
        break;
      case "loginAsUser":
        setSelectedUserForLoginAs(user);
        setConfirmLoginAsUserModal(true);
        break;
      default:
        break;
    }
  };

  // Role mapping for display names
  const getRoleDisplayName = (role?: string): string => {
    if (!role) return t("users.roleFallback");

    // We only have 4 roles: SUPER_ADMIN, ADMIN, TENANT_ADMIN, TENANT_USER
    const roleMap: Record<string, string> = {
      SUPER_ADMIN: t("common.roles.SUPER_ADMIN"),
      ADMIN: t("common.roles.ADMIN"),
      TENANT_ADMIN: t("common.roles.TENANT_ADMIN"),
      TENANT_USER: t("common.roles.TENANT_USER"),
    };

    return roleMap[role] || t("users.roleFallback");
  };

  const getAccountStatusBadge = (user: User): React.ReactElement => {
    const rawStatus = user.status || (user.locked ? "INACTIVE" : "ACTIVE");
    const normalized = rawStatus.toUpperCase();
    switch (normalized) {
      case "ACTIVE":
        return (
          <span className="status-badge active">
            {t("common.userStatus.ACTIVE")}
          </span>
        );
      case "INACTIVE":
        return (
          <span className="status-badge inactive-status">
            {t("common.userStatus.INACTIVE")}
          </span>
        );
      case "PENDING":
        return (
          <span className="status-badge pending">
            {t("common.userStatus.PENDING")}
          </span>
        );
      case "EXPIRED":
        return (
          <span className="status-badge expired">
            {t("common.userStatus.EXPIRED")}
          </span>
        );
      default:
        return (
          <span className="status-badge inactive-status">
            {t("users.statusUnknown")}
          </span>
        );
    }
  };

  // User Action Dropdown Component
  const UserActionDropdown: React.FC<{ user: User }> = ({ user }) => {
    const userStatus = (user.status || "").toUpperCase();
    const showResendInvite =
      (userStatus === "EXPIRED" || userStatus === "PENDING") && canResendInvite;
    const showActivate = userStatus === "INACTIVE" || userStatus === "EXPIRED";
    // Nobody can deactivate or delete their own account
    const isSelf = user.email === userProfile?.email;
    const showDeactivate = userStatus === "ACTIVE" && !isSelf;
    const showDelete = canDelete && !isSelf;

    // Check if user has any available actions
    const hasAnyAction =
      canViewLogs ||
      canEdit ||
      showResendInvite ||
      (canActivate && showActivate) ||
      (canDeactivate && showDeactivate) ||
      canLoginAs ||
      showDelete;

    // If no actions available, don't show dropdown
    if (!hasAnyAction) {
      return <span style={{ color: "#9e9e9e", fontSize: "14px" }}>-</span>;
    }

    return (
      <UncontrolledDropdown>
        <DropdownToggle
          tag="button"
          type="button"
          className="btn btn-row-actions"
          aria-label={t("users.actions.toggle")}
        >
          <i className="ri-more-2-fill fs-16" />
        </DropdownToggle>
        <DropdownMenu end>
          {canViewLogs && (
            <DropdownItem
              onClick={() => handleAction(user, "viewLogs")}
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <i className="ri-history-line me-2" />
              {t("users.actions.loginHistory")}
            </DropdownItem>
          )}
          {canEdit && (
            <DropdownItem
              onClick={() => handleAction(user, "manage")}
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <i className="ri-settings-3-line me-2" />
              {t("users.actions.manageUser")}
            </DropdownItem>
          )}
          {showResendInvite && (
            <DropdownItem
              onClick={() => handleAction(user, "resendInvite")}
              style={{
                display: "flex",
                alignItems: "center",
              }}
              disabled={resendInvitationMutation.isPending}
            >
              <i className="ri-mail-send-line me-2" />
              {t("users.actions.resendInvitation")}
            </DropdownItem>
          )}
          {canLoginAs && (
            <DropdownItem
              onClick={() => handleAction(user, "loginAsUser")}
              style={{
                display: "flex",
                alignItems: "center",
              }}
              disabled={impersonateUserMutation.isPending}
            >
              <i className="ri-user-shared-line me-2" />
              {t("users.actions.loginAsUser")}
            </DropdownItem>
          )}
          {canActivate && showActivate && (
            <DropdownItem
              onClick={() => handleAction(user, "activate")}
              style={{
                display: "flex",
                alignItems: "center",
              }}
              disabled={activateUserMutation.isPending}
            >
              <i className="ri-play-circle-line me-2" />
              {t("users.actions.activate")}
            </DropdownItem>
          )}
          {canDeactivate && showDeactivate && (
            <DropdownItem
              onClick={() => handleAction(user, "deactivate")}
              style={{
                display: "flex",
                alignItems: "center",
              }}
              disabled={deactivateUserMutation.isPending}
            >
              <i className="ri-pause-circle-line me-2" />
              {t("users.actions.deactivate")}
            </DropdownItem>
          )}
          {showDelete && (
            <DropdownItem
              onClick={() => handleAction(user, "delete")}
              style={{
                display: "flex",
                alignItems: "center",
                color: "#dc3545",
              }}
              disabled={deleteUsersMutation.isPending}
            >
              <i
                className="ri-delete-bin-line me-2"
                style={{ color: "#dc3545" }}
              />
              {t("users.actions.deleteUser")}
            </DropdownItem>
          )}
        </DropdownMenu>
      </UncontrolledDropdown>
    );
  };

  // Table columns configuration - conditionally include Action column based on permissions
  const baseColumns: Column<User>[] = [
    {
      // The signed-in user is marked "(you)".
      Header: t("common.labels.name"),
      accessor: "firstName" as any,
      width: "18%",
      Cell: ({ row }: any) => {
        const user = row.original as User;
        return (
          <div>
            {formatPersonName(user.firstName, user.lastName)}
            {user.email === userProfile?.email && (
              <span className="text-muted ms-1">{t("users.you")}</span>
            )}
          </div>
        );
      },
    },
    {
      Header: t("common.labels.email"),
      accessor: "email",
      width: "26%",
    },
    {
      Header: t("common.labels.role"),
      accessor: "role",
      width: "14%",
      Cell: ({ row }: any) => (
        <span className="badge bg-soft-primary text-primary fs-12">
          {getRoleDisplayName(row.original.role)}
        </span>
      ),
    },
    {
      Header: t("users.columns.accountStatus"),
      accessor: "status" as any,
      width: "16%",
      Cell: ({ row }: any) => getAccountStatusBadge(row.original),
    },
    {
      // Pending users have never logged in: show when they were invited.
      Header: t("users.columns.lastLogin"),
      accessor: "lastLogin" as any,
      width: "24%",
      Cell: ({ row }: any) => {
        const user = row.original as User;
        // The backend sets lastLogin when the user is invited, so go by status
        if (user.status?.toUpperCase() !== "PENDING")
          return user.lastLogin
            ? formatDateTime(user.lastLogin, i18n.language)
            : "-";
        if (user.createdAt)
          return (
            <span className="text-muted">
              {t("users.invitedOn", {
                // formatDay takes "YYYY-MM-DD"; en-CA gives that format in Taiwan time
                date: formatDay(
                  new Date(user.createdAt).toLocaleDateString("en-CA", {
                    timeZone: "Asia/Taipei",
                  }),
                  i18n.language
                ),
              })}
            </span>
          );
        return "-";
      },
    },
  ];

  // Add Action column only if user has any action permissions
  const hasAnyPermission =
    canViewLogs ||
    canEdit ||
    canResendInvite ||
    canActivate ||
    canDeactivate ||
    canLoginAs ||
    canDelete;

  const usersListColumns: Column<User>[] = hasAnyPermission
    ? [
        ...baseColumns,
        {
          Header: "",
          id: "actions",
          width: "10%",
          Cell: ({ row }: any) => {
            const user = row.original as User;
            return (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <UserActionDropdown user={user} />
              </div>
            );
          },
        },
      ]
    : baseColumns;

  // Status filter options
  const accountStatusOptions: SelectFilterOptionGroup[] = [
    {
      options: [
        { label: t("common.labels.all"), value: "all" },
        { label: t("common.userStatus.ACTIVE"), value: "active" },
        { label: t("common.userStatus.INACTIVE"), value: "inactive" },
        { label: t("common.userStatus.PENDING"), value: "pending" },
        { label: t("common.userStatus.EXPIRED"), value: "expired" },
      ],
    },
  ];

  // Extract data from API response (backend returns meta object)
  const total = (usersData as any)?.meta?.itemCount || 0;
  const hasMore = (usersData as any)?.meta?.hasNextPage || false;

  // Calculate pagination data
  const itemsPerPage = pageParams.rowsPerPage;
  const currentPage = pageParams.currentPage;
  const pageCount =
    (usersData as any)?.meta?.pageCount || Math.ceil(total / itemsPerPage);
  const hasPreviousPage =
    (usersData as any)?.meta?.hasPreviousPage || currentPage > 1;
  const hasNextPage = hasMore;

  document.title = t("common.pageTitle", { page: t("users.pageTitle") });

  return (
    <React.Fragment>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Create User Modal */}
      <UserModal
        show={addUsersModal}
        onClose={() => {
          setAddUsersModal(false);
          setApiError(null); // Clear error when closing
          setFailedUserData(null); // Clear failed data when closing
        }}
        onSubmit={handleCreateUser}
        isLoading={createUserMutation.isPending}
        error={apiError?.message}
        initialData={failedUserData}
      />

      {/* Edit User Modal */}
      <UserModal
        show={editUserModal}
        onClose={() => {
          setEditUserModal(false);
          setApiError(null);
        }}
        onSubmit={handleUpdateUser}
        isLoading={updateUserMutation.isPending}
        error={apiError?.message}
        editMode={true}
        initialData={
          selectedUserForEdit
            ? {
                firstName: selectedUserForEdit.firstName,
                lastName: selectedUserForEdit.lastName,
                email: selectedUserForEdit.email,
                role: selectedUserForEdit.role || "",
              }
            : null
        }
      />

      {/* Success Modal - User Created */}
      <UserModal
        show={successModal}
        onClose={() => setSuccessModal(false)}
        onSubmit={() => undefined}
        showSuccess={true}
        successType="created"
        onSuccessClose={() => {
          setSuccessModal(false);
          setApiError(null);
          setFailedUserData(null); // Clear form data after success
          setCreateErrorMessage(null); // Clear error message after success
        }}
      />

      {/* Error Modal - Create User Failed */}
      <UserModal
        show={createErrorModal}
        onClose={() => setCreateErrorModal(false)}
        onSubmit={() => undefined}
        showSuccess={true}
        successType="createError"
        successErrorMessage={createErrorMessage}
        onSuccessClose={() => {
          setCreateErrorModal(false);
        }}
        onRetry={() => {
          setCreateErrorModal(false);
          setAddUsersModal(true); // Reopen create modal with saved data
        }}
      />

      {/* Success Modal - Invitation Resent */}
      <UserModal
        show={resendSuccessModal}
        onClose={() => setResendSuccessModal(false)}
        onSubmit={() => undefined}
        showSuccess={true}
        successType="resent"
        onSuccessClose={() => {
          setResendSuccessModal(false);
        }}
      />

      {/* Error Modal - Resend Failed */}
      <UserModal
        show={resendErrorModal}
        onClose={() => setResendErrorModal(false)}
        onSubmit={() => undefined}
        showSuccess={true}
        successType="resendError"
        onSuccessClose={() => {
          setResendErrorModal(false);
        }}
      />

      {/* Delete Users Modal */}
      <ConfirmActionModal
        show={deleteUsersModal}
        onConfirm={handleConfirmDeleteUsers}
        onClose={handleCloseModals}
        title={t("users.confirm.delete.title")}
        message={t("users.confirm.delete.message")}
        confirmText={t("users.confirm.delete.confirm")}
        confirmColor="danger"
        isLoading={deleteUsersMutation.isPending}
        user={usersToDelete || undefined}
        tenant={undefined}
      />

      {/* Confirm Activate Modal */}
      <ConfirmActionModal
        show={confirmActivateModal}
        onConfirm={handleConfirmActivate}
        onClose={() => setConfirmActivateModal(false)}
        title={t("users.confirm.activate.title")}
        message={t("users.confirm.activate.message")}
        confirmText={t("users.confirm.activate.confirm")}
        confirmColor="success"
        isLoading={activateUserMutation.isPending}
        user={selectedUserForActivate || undefined}
        tenant={undefined}
      />

      {/* Confirm Deactivate Modal */}
      <ConfirmActionModal
        show={confirmDeactivateModal}
        onConfirm={handleConfirmDeactivate}
        onClose={() => setConfirmDeactivateModal(false)}
        title={t("users.confirm.deactivate.title")}
        message={t("users.confirm.deactivate.message")}
        confirmText={t("users.confirm.deactivate.confirm")}
        confirmColor="danger"
        isLoading={deactivateUserMutation.isPending}
        user={selectedUserForDeactivate || undefined}
        tenant={undefined}
      />

      {/* Confirm Login As User Modal */}
      <ConfirmActionModal
        show={confirmLoginAsUserModal}
        onConfirm={handleConfirmLoginAsUser}
        onClose={() => setConfirmLoginAsUserModal(false)}
        title={t("users.confirm.loginAs.title")}
        message={t("users.confirm.loginAs.message")}
        confirmText={t("users.confirm.loginAs.confirm")}
        confirmColor="primary"
        isLoading={impersonateUserMutation.isPending}
        user={selectedUserForLoginAs || undefined}
        tenant={undefined}
      />

      {/* Login Logs Modal */}
      <Modal
        isOpen={loginLogsModal}
        toggle={() => setLoginLogsModal(false)}
        size="lg"
        centered={true}
      >
        <ModalHeader toggle={() => setLoginLogsModal(false)}>
          {t("users.loginHistory.title", {
            name: formatPersonName(
              selectedUserForLogs?.firstName,
              selectedUserForLogs?.lastName
            ),
          })}
        </ModalHeader>
        <ModalBody>
          {isLoginLogsLoading ? (
            <div className="text-center">{t("users.loginHistory.loading")}</div>
          ) : (
            <Table responsive striped>
              <thead>
                <tr>
                  <th>{t("users.loginHistory.dateTime")}</th>
                  <th>{t("users.loginHistory.ipAddress")}</th>
                  <th>{t("users.loginHistory.device")}</th>
                  <th>{t("common.labels.status")}</th>
                </tr>
              </thead>
              <tbody>
                {(loginLogsData as any)?.data?.map((log: any) => (
                  <tr key={log.id}>
                    <td>
                      {log.timestamp
                        ? formatDateTime(log.timestamp, i18n.language)
                        : "-"}
                    </td>
                    <td>{log.ip}</td>
                    <td title={log.userAgent}>
                      {describeDevice(log.userAgent)}
                    </td>
                    <td>
                      <span
                        className={`status-badge ${
                          log.success ? "active" : "locked"
                        }`}
                      >
                        {log.success
                          ? t("users.loginHistory.success")
                          : t("users.loginHistory.failed")}
                      </span>
                      {/* Why a login failed; empty for successful ones */}
                      {!log.success && log.additionalInfo && (
                        <div className="text-muted fs-12 mt-1">
                          {log.additionalInfo}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            style={{
              background: "#757575",
              border: "none",
              fontFamily: "Poppins, sans-serif",
              fontWeight: 600,
              fontSize: "12px",
            }}
            onClick={() => setLoginLogsModal(false)}
          >
            {t("common.actions.close")}
          </Button>
        </ModalFooter>
      </Modal>

      <Row>
        <Col lg={12}>
          <Card id="usersList">
            <CardHeader className="border-0">
              <Row className="g-4 align-items-center">
                <div className="col-sm">
                  <div>
                    <h5 className="card-title mb-0">{t("users.heading")}</h5>
                  </div>
                </div>
                <div className="col-sm-auto">
                  {canCreate && (
                    <div>
                      <button
                        type="button"
                        className="btn btn-outline-primary add-btn"
                        id="create-btn"
                        onClick={handleOpenCreateUserModal}
                        style={{
                          backgroundColor: "#ffffff",
                          color: "#063a5e",
                          borderColor: "#063a5e",
                        }}
                      >
                        <i className="ri-user-add-fill align-bottom me-1"></i>
                        {t("users.inviteMember")}
                      </button>
                    </div>
                  )}
                </div>
              </Row>
            </CardHeader>

            <div className="card-body pt-0">
              {usersError && (
                <Alert color="danger" className="mb-3">
                  {translateApiError(
                    (usersError as any)?.message,
                    t("users.errors.loadFailed")
                  )}
                </Alert>
              )}
              <div>
                <TableContainer
                  columns={usersListColumns}
                  data={(usersData as any)?.data || []}
                  loading={isUsersLoading}
                  pageCount={pageCount}
                  hasPreviousPage={hasPreviousPage}
                  hasNextPage={hasNextPage}
                  pageParams={pageParams}
                  setPageParams={setPageParams}
                  filterParams={filterParams}
                  setFilterParams={setFilterParams}
                  disableRowModal={true}
                  datePicker={false}
                  selectFilterOptions={accountStatusOptions}
                  isGlobalFilter={true}
                  isGlobalSearch={true}
                  tableClass="custom-header-css"
                  theadClass="text-muted"
                  trClass={undefined}
                  thClass={undefined}
                  divClass={undefined}
                />
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default UsersList;
